import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { MaintenanceDetails } from '../../../interface/maintenance-scheduling.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchAllStationsList } from '../../../redux/slice/ApprovedStationManagementSlice';
import { fetchAllDevicesList } from '../../../redux/slice/DeviceManagementSlice';
import { fetchAllMaintenanceSchedules } from '../../../redux/slice/MaintenanceScheduleManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomDatePicker from '../../../shared-components/custom-date-range/CustomDatePicker';
import CustomTimeInput from '../../../shared-components/custom-time-input/CustomTimePicker';
import FormCancelWarning from '../../../shared-components/form-cancel-warning/FormCancelWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SelectFiled from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotificaion';

import MaintenanceContent from './MaintenanceContent';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * Add new maintenance
 */
function MaintenanceScheduling() {
  const dispatch = useAppDispatch();
  const { allStations, loading } = useAppSelector((state) => state.approvedStation);
  const { allDevices, loading: deviceLoading } = useAppSelector((state) => state.device);
  const { page, limit } = useAppSelector((state) => state.maintenanceSchedule);

  const navigate = useNavigate();
  const [maintenanceDetails, setMaintenanceDetails] = useState<MaintenanceDetails>({
    station: '',
    device: '',
    fromDate: '',
    time: '',
    maintenance: '',
    priority: '',
  });

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
  });
  const [showFormErrors, setShowFormErrors] = useState(false);

  const typeOfMaintenanceOptions = [
    {
      label: 'Inspection',
      value: 'inspection',
    },
    {
      label: 'Repair',
      value: 'repair',
    },
    {
      label: 'Software Update',
      value: 'softwareupdate',
    },
  ];
  const priorityOptions = [
    {
      label: 'High',
      value: 'high',
    },
    {
      label: 'Low',
      value: 'low',
    },
  ];

  /**
   * Fetch the all station function
   */
  const fetchStation = () => {
    if (!loading) {
      dispatch(fetchAllStationsList());
    }
  };
  /**
   * Fetch the all station function
   */
  const fetchDevices = (stationId: string | number) => {
    if (!deviceLoading) {
      dispatch(fetchAllDevicesList({ id: stationId }));
    }
  };

  /**
   * Handle input value change
   * @param name
   * @param changeValue
   */
  const handleMaintenanceDetailChange = (name: string, changeValue: string | number) => {
    setMaintenanceDetails({
      ...maintenanceDetails,
      [name]: changeValue,
    });
    if (name === 'station') {
      fetchDevices(changeValue);
    }
  };

  /**
   * Navigate back
   */
  const handleNavigateBack = () => {
    setShowFormErrors(false);
    setMaintenanceDetails({
      station: '',
      device: '',
      fromDate: '',
      time: '',
      maintenance: '',
      priority: '',
    });
    setModalBoxStatus('close');

    navigate('../schedule');
  };

  /**
   * Show warning when navigate back
   */
  const handleClickCancel = () => {
    setShowFormErrors(false);
    setModalBoxDetail({
      title: '',
      content: (
        <FormCancelWarning
          title="Are you sure want to Cancel?"
          description="Any unsaved changes will be loss"
          cancelButtonLabel="Cancel"
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitButtonLabel="OK"
          submitButtonOnClick={handleNavigateBack}
        />
      ),
      notificationModal: true,
    });
    setModalBoxStatus('open');
  };

  /**
   * Checks if all fields in the maintenance details are filled
   */
  const isFormComplete = () => {
    return Object.values(maintenanceDetails).every((value) => value !== '');
  };

  /**
   * Function to show success message
   */
  const openSuccessModal = () => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      content: (
        <SuccessNotification
          title="Maintenance Schedule Added Successfully..."
          description=""
          notificationCloseTimer={3000}
          onNotificationClose={() => {
            setModalBoxStatus('close');
            dispatch(
              fetchAllMaintenanceSchedules({
                page,
                limit,
              }),
            );
            navigate('../maintenance');
          }}
        />
      ),
      notificationModal: true,
    });
  };

  /**
   * Submit to open the modal
   */
  const handleSubmitMaintenanceDetails = () => {
    setShowFormErrors(true);
    if (!isFormComplete()) return; // Prevent submission if any field is empty

    setModalBoxDetail({
      title: 'Maintenance Scheduled',
      content: (
        <MaintenanceContent
          maintenanceDetails={maintenanceDetails}
          onChangeValue={handleMaintenanceDetailChange}
          closeModal={() => setModalBoxStatus('close')}
          openSuccessModal={openSuccessModal}
        />
      ),
      notificationModal: true,
    });
    setModalBoxStatus('open');
  };

  useEffect(() => {
    if (allStations.length === 0) {
      fetchStation();
    }
  }, []);

  return (
    <div className="add-maintenance-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <div className="add-maintenance-container">
        <div className="add-maintenance-from-wrap">
          <div className="add-maintenance-from">
            <SelectFiled
              inputLabel="Station Name"
              name="station"
              placeholder="Choose Station"
              selectedValue={maintenanceDetails.station}
              onChangeValue={handleMaintenanceDetailChange}
              options={allStations}
              error={!maintenanceDetails.station && showFormErrors}
              errorMessage="Station name is required"
            />
            <SelectFiled
              inputLabel="Device"
              name="device"
              placeholder="Choose Device"
              selectedValue={maintenanceDetails.device}
              onChangeValue={handleMaintenanceDetailChange}
              options={allDevices}
              error={!maintenanceDetails.device && showFormErrors}
              errorMessage="Device is required"
            />
            <CustomDatePicker
              name="fromDate"
              inputLabel="Date"
              value={maintenanceDetails.fromDate} // Replace with the appropriate value from your state
              onChangeValue={handleMaintenanceDetailChange} // Ensure this handles date changes
              error={!maintenanceDetails.fromDate && showFormErrors}
              errorMessage="Invalid date range"
            />
            <CustomTimeInput
              name="time"
              inputLabel="Time"
              value={maintenanceDetails.time}
              onChangeValue={handleMaintenanceDetailChange}
              errorHandle={!maintenanceDetails.time && showFormErrors}
              // errorMessage="Invalid time range"
            />
            <SelectFiled
              inputLabel="Type of Maintenance"
              name="maintenance"
              placeholder="Choose Type of Maintenance"
              selectedValue={maintenanceDetails.maintenance}
              onChangeValue={handleMaintenanceDetailChange}
              options={typeOfMaintenanceOptions}
              error={!maintenanceDetails.maintenance && showFormErrors}
              errorMessage="Type of maintenance is required"
            />
            <SelectFiled
              inputLabel="Priority"
              name="priority"
              placeholder="Choose Priority"
              selectedValue={maintenanceDetails.priority}
              onChangeValue={handleMaintenanceDetailChange}
              options={priorityOptions}
              error={!maintenanceDetails.priority && showFormErrors}
              errorMessage="Priority is required"
            />
          </div>
        </div>
        <div className="add-maintenance-button-wrap">
          <div className="form-buttons-wrap">
            <MyButton label="Cancel" buttonType="back" onClickFunc={handleClickCancel} />
            <MyButton
              label="Schedule"
              buttonType="submit"
              onClickFunc={handleSubmitMaintenanceDetails}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default MaintenanceScheduling;
